/* Copyright (c) 2020-present CloudIO Inc */

import type {
  AnyColumn,
  AnyFormulaColumn,
  Item,
  ItemID,
} from 'componentregistry';
import {
  emptyFunction,
  useStateXAtomSetter,
  useStateXAtomValue,
} from '@cloudio-saas/statex';
import {
  Divider,
  MenuList,
  MenuItem,
  Popover,
  IconButton,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import ColumnsSubMenu from 'components/DataGrid/ColumnsSubMenu';
import { Region } from 'components/DataGrid/grid/types';
import {
  columnMenuPositionAtom,
  ColumnMenuProps,
} from 'components/DataGrid/GridState';
import {
  SearchTextField as STF,
  SearchNumberField as SNF,
  SearchDateField as SDF,
} from 'componentregistry';
import {
  useAttributeSortState,
  useCurrentDataStore,
} from 'components/DataStoreReference/DataStoreReference';
import Icon from 'icons/Icon';
import {
  usePlatform,
  useRuntimeProperty,
  usePending,
  useItemProps,
} from 'platform/PlatformHooks';
import React, { memo } from 'react';
import WaveDots from 'utils/WaveDots';
import { defaultNewFormulaColumn } from 'components/DataGrid/FormulaColumnPopup';
import type { Platform } from 'componentregistry/platform/Platform';
import { useFormulaColumnRemover } from 'components/DataGrid/useColumns';
import { useTreeStateForStore } from 'components/DataGrid/columns/TreeColumn/useTreeState';
import { defaultValue } from 'componentregistry/components/DataGrid/DataGrid';
import { useTranslate } from '../../utils/lang';
import { useStaticRuntime } from '../../common/Context';
import { stopPropagation } from 'utils/events';
import SearchTextField from 'components/SearchTextField/SearchTextField';
import {
  DataRecord,
  isDateType,
  isNumberType,
  isStringType,
} from '@cloudio-saas/datasource-types';
import SearchNumberField from 'components/SearchNumberField/SearchNumberField';
import SearchDateField from 'components/SearchDateField/SearchDateField';
import SearchSelectField from 'components/SearchSelectField/SearchSelectField';
import SearchLookupField from 'components/SearchLookupField/SearchLookupField';
import SearchCheckbox from 'components/SearchCheckbox/SearchCheckbox';
import { isTriggerOrActionEnabled } from 'store/StoreTriggersUtils';
import { getFilterFromActions } from 'actions/ActionQuery/ActionQuery';

const useStyles = makeStyles(() => ({
  menuItem: {
    minWidth: 180,
  },
  icon: {
    marginRight: 12,
  },
  iconRight: {
    marginLeft: 12,
  },
}));

export interface MenuProps<T extends AnyColumn = AnyColumn> {
  active: boolean;
  column: Required<T>;
  columnIndex: number;
  editable: boolean;
  appUid: string;
  pageId: ItemID;
  itemId: ItemID;
  region: Region;
  rowIndex: number;
  platform: Platform;
  className: string;
}

interface Props extends ColumnMenuProps {
  resetScroll: () => void;
  totalColumns: number;
}

function ColumnMenu({
  appUid,
  column,
  gridItemId,
  left,
  pageId,
  resetScroll,
  top,
  totalColumns,
  roleUid,
}: Props): React.ReactElement | null {
  const classes = useStyles();
  const searchRef = React.useRef<HTMLButtonElement>(null);
  const setColumnMenuPosition = useStateXAtomSetter(columnMenuPositionAtom, {
    params: {
      appUid,
      pageId,
      gridItemId,
    },
  });
  const platform = usePlatform();
  let { t } = useTranslate();
  const staticRuntime = useStaticRuntime();
  if (staticRuntime) {
    t = (key) => key ?? '';
  }

  const store = useCurrentDataStore<DataRecord>();
  const [isPending1, setPending1] = usePending();
  const [isPending2, setPending2] = usePending();
  const [isPending3, setPending3] = usePending();

  const { expandAll, collapseAll } = useTreeStateForStore();

  function closeMenu() {
    setColumnMenuPosition(undefined);
  }

  const handleClose = (event: React.SyntheticEvent) => {
    event?.stopPropagation();
    closeMenu();
  };

  const { pinLeft, pinRight, sortable, filterable, viewAttribute } =
    column.props;
  const attr = store?.getAttribute(viewAttribute ?? 'x');
  let gridProps = useItemProps<'DataGrid' | 'CanvasGrid'>({
    appUid,
    pageId,
    itemId: gridItemId,
  });
  if (gridProps.insertable === undefined) {
    // must be LOVField
    gridProps = {
      ...defaultValue,
      pinnable: false,
      filterable: true,
      enableFormulaColumn: false,
    };
  }
  const sortState = useAttributeSortState(viewAttribute);
  const pinned = pinLeft || pinRight;

  const parent = platform.getItem(appUid, pageId, gridItemId);

  const [showFilters, setShowFilters] = useRuntimeProperty({
    appUid,
    itemId: gridItemId,
    pageId,
    prop: 'showFilters',
    defaultValue: false,
  });

  const [, setShowAddColumnPopup] = useRuntimeProperty({
    appUid,
    itemId: gridItemId,
    pageId,
    prop: 'showAddColumnPopup',
    defaultValue: false,
  });

  const [, setFormulaColumn] = useRuntimeProperty({
    appUid,
    itemId: gridItemId,
    pageId,
    prop: 'formulaColumn',
    defaultValue: defaultNewFormulaColumn,
  });

  const { deleteRecord } = useFormulaColumnRemover(appUid, pageId, gridItemId);

  if (!parent) {
    return null;
  }

  const isCanvasGrid = parent.type === 'CanvasGrid';
  // const { updateRuntimeProps } = useColumnsRuntimeData(
  //   pageId,
  //   dataContext.itemId,
  // );

  let sortIcons: [string, string] = ['sort-alpha-down', 'sort-alpha-down-alt'];
  switch (column.type) {
    case 'DateColumn':
      sortIcons = ['sort-amount-down-alt', 'sort-amount-down'];
      break;
    default:
  }

  async function doSearch() {
    await store?.query({});
    closeMenu();
  }

  const sortAllowed = sortable && gridProps.sortable && store && viewAttribute;
  const filterAllowed =
    filterable && gridProps.filterable && store && viewAttribute && attr;
  let filter;
  if (filterAllowed && attr && viewAttribute) {
    if (column.type === 'SelectColumn') {
      filter = (
        <SearchSelectField
          {...STF.defaultValue}
          defaultOperator="is"
          headerFilter
          viewAttribute={viewAttribute}
          appUid={appUid}
          pageId={pageId}
          platform={platform}
          itemId={column.itemId}
          autoFocus
          itemType="SearchSelectField"
          hideOperatorChooser={false}
          placeholder="Filter"
          domProps={{}}
          roleUid={roleUid}
          optionValues={column.props.optionValues}
          comboDatasourceAlias={column.props.comboDatasourceAlias}
          labelAttribute={column.props.labelAttribute}
          valueAttribute={column.props.valueAttribute}
          doNotPrefetch={column.props.doNotPrefetch}
          onHeaderFilterSearch={doSearch}
        />
      );
    } else if (column.type === 'LookupSelectColumn') {
      filter = (
        <SearchLookupField
          {...STF.defaultValue}
          defaultOperator="is"
          headerFilter
          viewAttribute={viewAttribute}
          appUid={appUid}
          pageId={pageId}
          platform={platform}
          itemId={column.itemId}
          autoFocus
          itemType="SearchLookupField"
          hideOperatorChooser={false}
          placeholder="Filter"
          domProps={{}}
          roleUid={roleUid}
          lookup={column.props.lookup}
          onHeaderFilterSearch={doSearch}
        />
      );
    } else if (['TF', 'YN', 'Boolean'].includes(attr.attributeType)) {
      filter = (
        <SearchCheckbox
          {...STF.defaultValue}
          defaultOperator="is"
          headerFilter
          viewAttribute={viewAttribute}
          appUid={appUid}
          pageId={pageId}
          platform={platform}
          itemId={column.itemId}
          autoFocus
          itemType="SearchCheckbox"
          hideOperatorChooser={false}
          placeholder="Filter"
          domProps={{}}
          roleUid={roleUid}
          onHeaderFilterSearch={doSearch}
        />
      );
    } else if (isStringType(attr.attributeType)) {
      filter = (
        <SearchTextField
          {...STF.defaultValue}
          defaultOperator="is"
          headerFilter
          viewAttribute={viewAttribute}
          appUid={appUid}
          pageId={pageId}
          platform={platform}
          itemId={column.itemId}
          autoFocus
          itemType="SearchTextField"
          hideOperatorChooser={false}
          placeholder="Filter"
          domProps={{}}
          roleUid={roleUid}
          onHeaderFilterSearch={doSearch}
        />
      );
    } else if (isNumberType(attr.attributeType)) {
      filter = (
        <SearchNumberField
          {...SNF.defaultValue}
          defaultOperator="eq"
          headerFilter
          viewAttribute={viewAttribute}
          appUid={appUid}
          pageId={pageId}
          platform={platform}
          itemId={column.itemId}
          autoFocus
          itemType="SearchNumberField"
          hideOperatorChooser={false}
          placeholder="Filter"
          domProps={{}}
          roleUid={roleUid}
          onHeaderFilterSearch={doSearch}
        />
      );
    } else if (isDateType(attr.attributeType) && column.type === 'DateColumn') {
      filter = (
        <SearchDateField
          {...SDF.defaultValue}
          defaultOperator="on"
          headerFilter
          viewAttribute={viewAttribute}
          appUid={appUid}
          pageId={pageId}
          platform={platform}
          itemId={column.itemId}
          autoFocus
          itemType="SearchDateField"
          hideOperatorChooser={false}
          placeholder="Filter"
          domProps={{}}
          roleUid={roleUid}
          onHeaderFilterSearch={doSearch}
          showTime={column.props.showTime && attr.attributeType === 'DateTime'}
        />
      );
    }
  }

  function updateCheckStatus(e: React.MouseEvent<HTMLElement>, flag: boolean) {
    e.stopPropagation();
    if (column.type === 'CheckboxColumn') {
      const checkBoxColumn = column.props.viewAttribute;
      if (store && checkBoxColumn) {
        if (!store.isReadonly()) {
          const attribute = store.getAttribute(checkBoxColumn);
          if (
            attribute?.attributeType === 'YN' ||
            attribute?.attributeType === 'String'
          ) {
            const checkedValue = column.props.checkedValue || 'Y';
            const unCheckedValue = column.props.unCheckedValue || 'N';
            store.recordsOrder().forEach((id) => {
              store.updateRecord(id, {
                [checkBoxColumn]: flag ? checkedValue : unCheckedValue,
              });
            });
          } else if (attribute?.attributeType === 'TF') {
            const checkedValue = column.props.checkedValue || 'T';
            const unCheckedValue = column.props.unCheckedValue || 'F';
            store.recordsOrder().forEach((id) => {
              store.updateRecord(id, {
                [checkBoxColumn]: flag ? checkedValue : unCheckedValue,
              });
            });
          } else if (attribute?.attributeType === 'Boolean') {
            store.recordsOrder().forEach((id) => {
              store.updateRecord(id, { [checkBoxColumn]: flag });
            });
          }
        }
      }
    }
    handleClose(e);
  }

  function doSort(
    e: React.MouseEvent<HTMLElement>,
    dir: number,
    setPending: (pending: boolean) => void,
  ) {
    e.stopPropagation();
    if (sortAllowed) {
      platform.startAsyncTransition(
        async () => {
          // resetScroll();
          let beforeQuerySort = {};
          if (store.itemId) {
            const triggerBeforeQueryItems = store.platform.getChildrenItemsById(
              store.appUid,
              store.pageId,
              store.itemId,
              (child) =>
                (child.type === 'TriggerBeforeQuery' ||
                  child.type === 'TriggerBeforeLoad') &&
                isTriggerOrActionEnabled(
                  child,
                  store.platform,
                  store.pageId,
                  store.appUid,
                ),
            ) as Item<'TriggerBeforeQuery'>[];
            if (triggerBeforeQueryItems.length) {
              const len = triggerBeforeQueryItems.length;
              for (let i = 0; i < len; i++) {
                const item = triggerBeforeQueryItems[i];
                const { sort } = getFilterFromActions({
                  appUid: store.appUid,
                  platform: store.platform,
                  store,
                  pageId: store.pageId,
                  itemId: item.itemId,
                  extraProps: {},
                  showEmptyFilterWarning: false,
                });
                if (Object.keys(sort).length) {
                  beforeQuerySort = {
                    ...beforeQuerySort,
                    ...sort,
                  };
                }
              }
            }
          }
          const sort = Object.keys(beforeQuerySort).length
            ? { ...beforeQuerySort, ...(dir && { [viewAttribute]: dir }) }
            : dir
            ? { [viewAttribute]: dir }
            : {};
          await store?.sort(sort);
          setColumnMenuPosition(undefined);
        },
        setPending,
        { itemId: gridItemId, pageId, appUid },
      );

      if (platform.isRecording()) {
        if (dir) {
          platform.recordLine(
            column.itemId,
            `await ui.gridColumnSort('${column.itemId}', { direction: '${
              dir === -1 ? 'desc' : 'asc'
            }' });`,
          );
        } else {
          platform.recordLine(
            column.itemId,
            `await ui.gridColumnSort('${column.itemId}');`,
          );
        }
      }
    }
  }

  const checkAll =
    column.type === 'CheckboxColumn' ? (
      <MenuItem
        data-testid="checkAll"
        onClick={(e) => updateCheckStatus(e, true)}
      >
        {<Icon className={classes.icon} iconName="check-square" />}
        {t('Check All')}
      </MenuItem>
    ) : null;

  const unCheckAll =
    column.type === 'CheckboxColumn' ? (
      <MenuItem
        data-testid="unCheckAll"
        disabled={sortState === -1}
        onClick={(e) => updateCheckStatus(e, false)}
      >
        {<Icon className={classes.icon} iconName="square" />}
        {t('UnCheck All')}
      </MenuItem>
    ) : null;

  const filterMenuItem = filter ? (
    <MenuItem
      disableRipple
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
      }}
      data-testid="sort-asc"
      sx={{
        '&:hover': {
          backgroundColor: 'background.paper',
        },
        '&.Mui-focusVisible': {
          backgroundColor: 'background.paper',
        },
        padding: 0,
        maxWidth: '100%',
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          handleClose(e);
        }
        e.stopPropagation();
      }}
    >
      {filter}
      <IconButton
        ref={searchRef}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          doSearch();
        }}
      >
        <Icon iconName="search" />
      </IconButton>
    </MenuItem>
  ) : null;

  const ascSortMenuItem = sortAllowed ? (
    <MenuItem
      disabled={sortState === 1}
      onClick={(e) => doSort(e, 1, setPending1)}
      data-testid="sort-asc"
    >
      {isPending1 && sortState !== 1 ? (
        <WaveDots active style={{ margin: '0 8px' }} />
      ) : (
        <Icon className={classes.icon} iconName={sortIcons[0]} />
      )}
      {t('Sort Ascending')}
    </MenuItem>
  ) : null;
  const descSortMenuItem = sortAllowed ? (
    <MenuItem
      data-testid="sort-desc"
      disabled={sortState === -1}
      onClick={(e) => doSort(e, -1, setPending2)}
    >
      {isPending2 && sortState !== -1 ? (
        <WaveDots active style={{ margin: '0 8px' }} />
      ) : (
        <Icon className={classes.icon} iconName={sortIcons[1]} />
      )}
      {t('Sort Descending')}
    </MenuItem>
  ) : null;

  const clearSortMenuItem =
    sortAllowed && sortState ? (
      <MenuItem
        onClick={(e) => doSort(e, 0, setPending3)}
        data-testid="clear-sort"
      >
        {isPending3 ? (
          <WaveDots active style={{ margin: '0 8px' }} />
        ) : (
          <Icon className={classes.icon} iconName="times" />
        )}
        {t('Clear Sort')}
      </MenuItem>
    ) : null;

  const isFormulaColumn = [
    'FormulaStringColumn',
    'FormulaNumberColumn',
  ].includes(column.type);

  const isTreeColumn = column.type === 'TreeColumn';

  const FormulaColumnDeleteMenuItem = isFormulaColumn ? (
    <MenuItem
      onClick={(event) => {
        deleteRecord(column.itemId);
        handleClose(event);
      }}
    >
      <Icon className={classes.icon} iconName="trash" />
      {t('Delete Formula Column')}
    </MenuItem>
  ) : null;

  const FormulaColumnCreateMenuItem = gridProps.enableFormulaColumn ? (
    <MenuItem
      data-testid="add-formula-column"
      onClick={(event) => {
        setShowAddColumnPopup(true);
        setFormulaColumn({
          ...defaultNewFormulaColumn,
          parent: column.itemId,
        });
        handleClose(event);
      }}
    >
      <Icon className={classes.icon} iconName="sigma" />
      {t('Add Formula Column')}
    </MenuItem>
  ) : null;

  const FormulaColumnUpdateMenuItem = isFormulaColumn ? (
    <MenuItem
      data-testid="edit-formula-column"
      onClick={(event) => {
        setShowAddColumnPopup(true);
        setFormulaColumn({
          ...column,
        } as AnyFormulaColumn);
        handleClose(event);
      }}
    >
      <Icon className={classes.icon} iconName="edit" />
      {t('Edit Formula Column')}
    </MenuItem>
  ) : null;

  const TreeColumnMenu = isTreeColumn ? (
    <>
      <MenuItem
        data-testid="expand-all"
        onClick={(event) => {
          expandAll();
          handleClose(event);
        }}
      >
        <Icon className={classes.icon} iconName="expand-alt" />
        {t('Expand All')}
      </MenuItem>
      <MenuItem
        data-testid="collapse-all"
        onClick={(event) => {
          collapseAll();
          handleClose(event);
        }}
      >
        <Icon className={classes.icon} iconName="compress-alt" />
        {t('Collapse All')}
      </MenuItem>
    </>
  ) : null;

  return (
    <Popover
      anchorReference="anchorPosition"
      anchorPosition={{ left, top }}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      open
      onClose={handleClose}
      className="click-outside-ignore"
      onMouseMove={stopPropagation}
      onMouseDown={stopPropagation}
      onMouseUp={stopPropagation}
      onClick={stopPropagation}
      // sx={{ zIndex: zIndex.Tooltip }}
    >
      <MenuList>
        {filterMenuItem}
        {ascSortMenuItem}
        {descSortMenuItem}
        {clearSortMenuItem}
        {checkAll}
        {unCheckAll}
        {sortAllowed ? <Divider /> : null}
        {pinned && gridProps.pinnable ? (
          <MenuItem
            data-testid="unpin"
            onClick={(event) => {
              event.stopPropagation();
              setColumnMenuPosition(undefined);
              platform.startAsyncTransition(async () => {
                if (pinLeft) {
                  platform.setRuntimeProp(
                    appUid,
                    pageId,
                    column.itemId,
                    'pinLeft',
                    false,
                  );
                }
                if (pinRight) {
                  platform.setRuntimeProp(
                    appUid,
                    pageId,
                    column.itemId,
                    'pinRight',
                    false,
                  );
                }
              }, emptyFunction);
            }}
          >
            <Icon className={classes.icon} iconName="thumbtack" />
            {t('Unpin')}
          </MenuItem>
        ) : null}
        {!pinLeft && gridProps.pinnable ? (
          <MenuItem
            data-testid="pin-left"
            onClick={(event) => {
              event.stopPropagation();
              platform.startAsyncTransition(async () => {
                setColumnMenuPosition(undefined);
                platform.setRuntimeProp(
                  appUid,
                  pageId,
                  column.itemId,
                  'pinLeft',
                  true,
                );
              }, emptyFunction);
            }}
          >
            <Icon className={classes.icon} iconName="thumbtack" />
            {t('Pin Left')}
          </MenuItem>
        ) : null}
        {!pinRight && gridProps.pinnable && !isCanvasGrid ? (
          <MenuItem
            data-testid="pin-right"
            onClick={(event) => {
              event.stopPropagation();
              setColumnMenuPosition(undefined);
              platform.setRuntimeProp(
                appUid,
                pageId,
                column.itemId,
                'pinLeft',
                false,
              );
              platform.setRuntimeProp(
                appUid,
                pageId,
                column.itemId,
                'pinRight',
                true,
              );
            }}
          >
            <Icon className={classes.icon} iconName="thumbtack" />
            {t('Pin Right')}
          </MenuItem>
        ) : null}
        <MenuItem
          data-testid="hide-column"
          disabled={totalColumns === 1}
          onClick={(event) => {
            event.stopPropagation();
            setColumnMenuPosition(undefined);
            platform.setRuntimeProp(
              appUid,
              pageId,
              column.itemId,
              'visible',
              false,
            );
            // updateRuntimeProps(column.itemId, { visible: false });
          }}
        >
          <Icon className={classes.icon} iconName="eye-slash" color="error" />
          {t('Hide Column')}
        </MenuItem>
        {gridProps.filterable && filterable && !isCanvasGrid ? (
          <MenuItem
            data-testid={showFilters ? 'hide-filters' : 'show-filters'}
            onClick={(event) => {
              if (platform.isRecording()) {
                platform.recordLine(
                  column.itemId,
                  `await ui.${showFilters ? 'hide' : 'show'}GridHeaderFilter('${
                    column.itemId
                  }');`,
                );
              }

              setShowFilters(!showFilters);
              handleClose(event);
            }}
          >
            <Icon className={classes.icon} iconName="filter" />
            {showFilters ? t('Hide Filters') : t('Show Filters')}
          </MenuItem>
        ) : null}
        {FormulaColumnCreateMenuItem}
        {FormulaColumnUpdateMenuItem}
        {FormulaColumnDeleteMenuItem}
        {TreeColumnMenu}
        <Divider />
        <ColumnsSubMenu
          gridItemId={column.parent}
          appUid={appUid}
          pageId={pageId}
          platform={platform}
        />
      </MenuList>
    </Popover>
  );
}

function ColumnMenuWrapper({
  appUid,
  gridItemId,
  pageId,
  resetScroll,
  totalColumns,
}: {
  appUid: string;
  gridItemId: string;
  pageId: string;
  resetScroll: () => void;
  totalColumns: number;
}) {
  const props = useStateXAtomValue(columnMenuPositionAtom, {
    params: {
      appUid,
      pageId,
      gridItemId,
    },
  });
  return props ? (
    <ColumnMenu
      {...props}
      resetScroll={resetScroll}
      totalColumns={totalColumns}
    />
  ) : null;
}

export default memo(ColumnMenuWrapper);
